import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { PiggyBank } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type Region = "uk" | "us"
type UKTaxBand = "basic" | "higher"
type FilingStatus = "single" | "married"

export default function CapitalGainsTaxEstimatorPage() {
  const { tool } = useOutletContext<{ tool: ToolDef; currency: string }>()

  const [region, setRegion] = useState<Region>("uk")

  // Shared inputs
  const [purchasePriceStr, setPurchasePriceStr] = useState("")
  const [purchaseCostsStr, setPurchaseCostsStr] = useState("")
  const [improvementsStr, setImprovementsStr] = useState("")
  const [salePriceStr, setSalePriceStr] = useState("")
  const [saleCostsStr, setSaleCostsStr] = useState("")
  const [holdingPeriod, setHoldingPeriod] = useState<"short" | "long">("long")

  // UK-specific
  const [ukTaxBand, setUKTaxBand] = useState<UKTaxBand>("higher")
  const [annualAllowanceStr, setAnnualAllowanceStr] = useState("3000")

  // US-specific
  const [filingStatus, setFilingStatus] = useState<FilingStatus>("single")
  const [annualIncomeStr, setAnnualIncomeStr] = useState("")
  const [depreciationClaimedStr, setDepreciationClaimedStr] = useState("")

  const results = useMemo(() => {
    const purchasePrice = parseFloat(purchasePriceStr) || 0
    const purchaseCosts = parseFloat(purchaseCostsStr) || 0
    const improvements = parseFloat(improvementsStr) || 0
    const salePrice = parseFloat(salePriceStr) || 0
    const saleCosts = parseFloat(saleCostsStr) || 0

    if (purchasePrice <= 0 || salePrice <= 0) return null

    const adjustedBasis = purchasePrice + purchaseCosts + improvements
    const netSaleProceeds = salePrice - saleCosts
    const capitalGain = netSaleProceeds - adjustedBasis

    if (region === "uk") {
      const allowance = parseFloat(annualAllowanceStr) || 3000
      const taxableGain = Math.max(0, capitalGain - allowance)
      const rate = ukTaxBand === "basic" ? 0.18 : 0.24
      const cgtOwed = holdingPeriod === "short" ? taxableGain * 0.18 : taxableGain * rate
      const netProceeds = netSaleProceeds - cgtOwed

      return {
        region: "uk" as const,
        sym: "£",
        capitalGain,
        adjustedBasis,
        netSaleProceeds,
        allowanceUsed: Math.min(allowance, Math.max(0, capitalGain)),
        taxableGain,
        rate: holdingPeriod === "short" ? 0.18 : rate,
        taxOwed: cgtOwed,
        netProceeds,
        effectiveRate: capitalGain > 0 ? (cgtOwed / capitalGain) * 100 : 0,
      }
    }

    // US
    const income = parseFloat(annualIncomeStr) || 0
    const depreciationClaimed = parseFloat(depreciationClaimedStr) || 0

    let ltRate: number
    if (holdingPeriod === "short") {
      // Short-term taxed as ordinary income — simplified brackets
      ltRate = income < 44725 ? 0.12 : income < 95375 ? 0.22 : income < 182050 ? 0.24 : 0.32
    } else if (filingStatus === "single") {
      ltRate = income < 47025 ? 0 : income < 518900 ? 0.15 : 0.20
    } else {
      // Married filing jointly
      ltRate = income < 94050 ? 0 : income < 583750 ? 0.15 : 0.20
    }

    const depRecapture = Math.min(depreciationClaimed, Math.max(0, capitalGain)) * 0.25
    const gainAfterRecapture = Math.max(0, capitalGain - depreciationClaimed)
    const niit = income > (filingStatus === "single" ? 200000 : 250000) ? gainAfterRecapture * 0.038 : 0
    const baseGainTax = holdingPeriod === "short" ? capitalGain * ltRate : gainAfterRecapture * ltRate
    const totalTax = baseGainTax + depRecapture + niit
    const netProceeds = netSaleProceeds - totalTax

    return {
      region: "us" as const,
      sym: "$",
      capitalGain,
      adjustedBasis,
      netSaleProceeds,
      taxOwed: totalTax,
      netProceeds,
      effectiveRate: capitalGain > 0 ? (totalTax / capitalGain) * 100 : 0,
      ltRate,
      depRecapture,
      niit,
      baseGainTax,
    }
  }, [
    region, purchasePriceStr, purchaseCostsStr, improvementsStr,
    salePriceStr, saleCostsStr, holdingPeriod,
    ukTaxBand, annualAllowanceStr,
    filingStatus, annualIncomeStr, depreciationClaimedStr,
  ])

  const fmt = (n: number, sym: string) => `${sym}${Math.round(Math.abs(n)).toLocaleString()}`

  const sym = region === "uk" ? "£" : "$"

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6 space-y-5">

          {/* Region Toggle */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Tax Jurisdiction</p>
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
              <button
                onClick={() => setRegion("uk")}
                className={`px-6 py-2 text-sm font-medium transition-colors ${region === "uk" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                🇬🇧 United Kingdom
              </button>
              <button
                onClick={() => setRegion("us")}
                className={`px-6 py-2 text-sm font-medium transition-colors ${region === "us" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                🇺🇸 United States (Federal)
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              {([
                ["Original Purchase Price", purchasePriceStr, setPurchasePriceStr, "e.g. 250000"],
                ["Purchase Costs (legal, stamp duty, etc.)", purchaseCostsStr, setPurchaseCostsStr, "e.g. 8000"],
                ["Improvements Made", improvementsStr, setImprovementsStr, "Optional — renovations, extensions"],
                ["Sale Price", salePriceStr, setSalePriceStr, "e.g. 420000"],
                ["Sale Costs (agent fees, legal)", saleCostsStr, setSaleCostsStr, "e.g. 12000"],
              ] as const).map(([label, val, setter, ph]) => (
                <div key={label}>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{sym}</span>
                    <input
                      type="number"
                      min="0"
                      value={val}
                      onChange={(e) => (setter as (v: string) => void)(e.target.value)}
                      placeholder={ph}
                      className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              ))}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Holding Period</label>
                <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
                  <button
                    onClick={() => setHoldingPeriod("long")}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${holdingPeriod === "long" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    Long-Term (&gt;1yr)
                  </button>
                  <button
                    onClick={() => setHoldingPeriod("short")}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${holdingPeriod === "short" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  >
                    Short-Term (&lt;1yr)
                  </button>
                </div>
              </div>

              {/* UK-specific */}
              {region === "uk" && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Your Income Tax Band</label>
                    <select
                      value={ukTaxBand}
                      onChange={(e) => setUKTaxBand(e.target.value as UKTaxBand)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="basic">Basic Rate (18% CGT)</option>
                      <option value="higher">Higher / Additional Rate (24% CGT)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Annual CGT Allowance</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                      <input
                        type="number"
                        value={annualAllowanceStr}
                        onChange={(e) => setAnnualAllowanceStr(e.target.value)}
                        placeholder="3000"
                        className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">£3,000 for 2024/25 tax year</p>
                  </div>
                </>
              )}

              {/* US-specific */}
              {region === "us" && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Filing Status</label>
                    <select
                      value={filingStatus}
                      onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    >
                      <option value="single">Single</option>
                      <option value="married">Married Filing Jointly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Annual Income (approx.)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        value={annualIncomeStr}
                        onChange={(e) => setAnnualIncomeStr(e.target.value)}
                        placeholder="e.g. 90000"
                        className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Used to determine your long-term CGT rate bracket</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Depreciation Previously Claimed (Optional)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        value={depreciationClaimedStr}
                        onChange={(e) => setDepreciationClaimedStr(e.target.value)}
                        placeholder="e.g. 30000"
                        className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Subject to 25% recapture tax at sale</p>
                  </div>
                </>
              )}
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

                {!results ? (
                  <p className="text-slate-400 text-sm text-center py-10">Enter purchase and sale prices to estimate tax</p>
                ) : (
                  <>
                    {results.capitalGain < 0 ? (
                      <div className="text-center py-4 bg-green-50 rounded-xl border border-green-200">
                        <p className="text-2xl font-extrabold text-green-700">Capital Loss</p>
                        <p className="text-sm text-green-600 mt-1">
                          {fmt(Math.abs(results.capitalGain), sym)} loss — no CGT owed
                        </p>
                        <p className="text-xs text-green-500 mt-1">Loss may be used to offset other gains</p>
                      </div>
                    ) : (
                      <>
                        <div className="text-center py-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Estimated Tax Owed</p>
                          <p className="text-5xl font-extrabold text-red-500">{fmt(results.taxOwed, sym)}</p>
                          <p className="text-sm text-slate-500 mt-1">Effective rate on gain: {results.effectiveRate.toFixed(1)}%</p>
                        </div>

                        <div className="border-t border-primary/10 pt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Adjusted Cost Basis</span>
                            <span className="font-semibold text-slate-900">{fmt(results.adjustedBasis, sym)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Net Sale Proceeds</span>
                            <span className="font-semibold text-slate-900">{fmt(results.netSaleProceeds, sym)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Capital Gain</span>
                            <span className="font-semibold text-slate-900">{fmt(results.capitalGain, sym)}</span>
                          </div>

                          {results.region === "uk" && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Annual Allowance Used</span>
                                <span className="font-semibold text-slate-900">− {fmt(results.allowanceUsed, sym)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Taxable Gain</span>
                                <span className="font-semibold text-slate-900">{fmt(results.taxableGain, sym)}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">CGT Rate Applied</span>
                                <span className="font-semibold text-slate-900">{(results.rate * 100).toFixed(0)}%</span>
                              </div>
                            </>
                          )}

                          {results.region === "us" && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Base Gain Tax ({(results.ltRate * 100).toFixed(0)}%)</span>
                                <span className="font-semibold text-slate-900">{fmt(results.baseGainTax, sym)}</span>
                              </div>
                              {results.depRecapture > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Depreciation Recapture (25%)</span>
                                  <span className="font-semibold text-red-600">{fmt(results.depRecapture, sym)}</span>
                                </div>
                              )}
                              {results.niit > 0 && (
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Net Investment Income Tax (3.8%)</span>
                                  <span className="font-semibold text-red-600">{fmt(results.niit, sym)}</span>
                                </div>
                              )}
                            </>
                          )}

                          <div className="border-t border-primary/10 pt-2 flex justify-between text-sm font-bold">
                            <span className="text-slate-700">Net Proceeds After Tax</span>
                            <span className="text-green-600">{fmt(results.netProceeds, sym)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Tip Boxes */}
              {region === "uk" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Private Residence Relief</p>
                  <p className="text-xs text-blue-700">If you lived in this property as your main residence for any period, you may qualify for Private Residence Relief, which could significantly reduce or eliminate your CGT liability.</p>
                </div>
              )}
              {region === "us" && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-blue-800 mb-1">1031 Exchange</p>
                  <p className="text-xs text-blue-700">Consider a 1031 Exchange to defer federal capital gains tax by reinvesting proceeds into a like-kind property within 180 days of sale.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Adjusted Basis = Purchase Price + Purchase Costs + Improvements\nNet Sale Proceeds = Sale Price − Sale Costs\nCapital Gain = Net Sale Proceeds − Adjusted Basis\n\nUK: Taxable Gain = Capital Gain − Annual Allowance (£3,000)\n    CGT = Taxable Gain × Rate (18% basic / 24% higher)\n\nUS: Base Tax = (Capital Gain − Depreciation Recapture) × LT Rate\n    + Depreciation Recapture × 25%\n    + NIIT (3.8% if income > $200k single / $250k joint)`}
        description="Both jurisdictions allow you to add purchase costs, legal fees, and capital improvements to your cost basis — reducing the taxable gain. Keep all receipts for improvements."
      />

      <ToolFAQ
        items={[
          {
            q: "What costs can I add to my cost basis to reduce my CGT?",
            a: "In both the UK and US, you can add to your adjusted basis: the original purchase price, purchase costs (solicitor/legal fees, stamp duty), and capital improvements (extensions, structural renovations). Routine repairs and maintenance cannot be added.",
          },
          {
            q: "What is depreciation recapture in the US?",
            a: "When you sell a US rental property, the IRS requires you to pay a 25% tax on all depreciation deductions previously claimed, regardless of your normal capital gains rate. This is called depreciation recapture and is a critical factor in calculating net sale proceeds.",
          },
          {
            q: "Can I reduce CGT on a former UK rental property?",
            a: "Yes, potentially. If you lived in the property as your main residence for any period, you may qualify for Private Residence Relief. Lettings Relief may also apply in some cases. Consult a qualified tax adviser for your specific situation.",
          },
        ]}
      />

      <CTABox
        icon={PiggyBank}
        heading="Log capital improvements to reduce future tax"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
